import {
  IInfoCategoryMasterData,
  useGetInfoCategoryMasterData,
} from '@Domains/MasterData';
import { arrange, asc, groupBy, tidy } from '@tidyjs/tidy';
import { AxiosError } from 'axios';
import { RefetchFunction } from 'axios-hooks';
import { createContext, useContext } from 'react';
import { INFO_CATEGORY_MASTER_DATA as MASTER_DATA } from 'src/Enum/MasterData';

type GreenhouseGoalTermProviderProps = {
  children: React.ReactNode;
};

type GreenhouseGoalTermContextProps = {
  ghGasScope?: IInfoCategoryMasterData[];
  certTgo?: IInfoCategoryMasterData[];
  sbtiGasTarget?: IInfoCategoryMasterData[];
  deptCarbonNeutral?: IInfoCategoryMasterData[];
  loading: boolean;
  error?: AxiosError | null;
  refetch?: RefetchFunction<unknown, IInfoCategoryMasterData[]>;
};

const GreenhouseGoalTermContext = createContext<GreenhouseGoalTermContextProps>(
  { loading: false }
);

const GreenhouseGoalTermProvider = (props: GreenhouseGoalTermProviderProps) => {
  const { children } = props;

  const [{ data = [], loading, error }, refetch] = useGetInfoCategoryMasterData(
    [
      MASTER_DATA.GH_GAS_SCOPE,
      MASTER_DATA.CERTIFICATE_TGO,
      MASTER_DATA.SBTI_GH_GAS_TARGET,
      MASTER_DATA.DEPT_CARBON_NEUTRAL,
    ]
  );

  const {
    GH_GAS_SCOPE: ghGasScope,
    CERTIFICATE_TGO: certTgo,
    SBTI_GH_GAS_TARGET: sbtiGasTarget,
    DEPT_CARBON_NEUTRAL: deptCarbonNeutral,
  } = tidy(
    data,
    arrange([asc('sequence')]),
    groupBy('infoCategory', groupBy.object())
  );

  return (
    <GreenhouseGoalTermContext.Provider
      value={{
        ghGasScope,
        certTgo,
        sbtiGasTarget,
        deptCarbonNeutral,
        loading,
        error,
        refetch,
      }}
    >
      {children}
    </GreenhouseGoalTermContext.Provider>
  );
};

const useGreenhouseGoalTerm = () => {
  const context = useContext(GreenhouseGoalTermContext);

  if (!context) {
    throw new Error(
      'useGreenhouseGoalTerm must be used within a GreenhouseGoalTermProvider'
    );
  }

  return context;
};

export { useGreenhouseGoalTerm };
export default GreenhouseGoalTermProvider;
